import { memo, useCallback, useEffect, useRef, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import {
  ChartBarIcon,
  ChevronRightIcon,
  HeartIcon,
  InformationCircleIcon,
  ShoppingBagIcon,
  SparklesIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import qumashiLogo from '../../../assets/qumashi-logo.webp';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { selectCartItemCount } from '../../../store/cartSlice';
import { selectWishlistCount } from '../../../store/wishlistSlice';
import {
  NAV_BADGE_CART_DRAWER,
  NAV_BADGE_WISHLIST_DRAWER,
} from '../../../config/headerNavBadges';
import { ACCOUNT_WISHLIST_PATH } from '../../../config/accountPaths';
import { MobileAccountIdentity } from '../MobileAccountIdentity';
import { useNavbarBadges } from '../../../hooks/useNavbarBadges';
import { useAccountSheet } from '../../../contexts/AccountSheetContext';
import { type MainNavLink } from '../../../config/navbarChrome';
import { useNavPersona } from '../../../contexts/NavPersonaContext';
import { getRoutePrefetchHandlers } from '../../../utils/routePrefetch';
import { requestCartHydration } from '../../../hooks/useEnsureCartLoaded';

type NavLinkMeta = {
  icon: React.ComponentType<{ className?: string }>;
  subtitle: string;
  featured?: boolean;
};

const NAV_LINK_META: Record<string, NavLinkMeta> = {
  '/owner': {
    icon: ChartBarIcon,
    subtitle: 'Platform overview & priorities',
    featured: true,
  },
  '/shop': {
    icon: ShoppingBagIcon,
    subtitle: 'Browse drops & collections',
    featured: true,
  },
  '/design-tool': {
    icon: SparklesIcon,
    subtitle: 'Create something uniquely yours',
  },
  '/creator-program': {
    icon: SparklesIcon,
    subtitle: 'Promote products & earn commission',
  },
  '/about': {
    icon: InformationCircleIcon,
    subtitle: 'Our story & how we work',
  },
};

function getLinkMeta(path: string): NavLinkMeta {
  return (
    NAV_LINK_META[path] ?? {
      icon: ChevronRightIcon,
      subtitle: 'Explore',
    }
  );
}

type NavbarMobileMenuProps = {
  isOpen: boolean;
  onClose: () => void;
  /** Set synchronously on open so ghost-click guards work before paint. */
  openedAtRef: RefObject<number>;
};

const GHOST_CLICK_MS = 350;

export const NavbarMobileMenu = memo(function NavbarMobileMenu({
  isOpen,
  onClose,
  openedAtRef,
}: NavbarMobileMenuProps) {
  const location = useLocation();
  const pathnameRef = useRef(location.pathname);
  const { open: openAccountSheet } = useAccountSheet();
  const { isAuthenticated, user } = useAppSelector((state) => state.auth);
  const cartItemCount = useAppSelector(selectCartItemCount);
  const wishlistCount = useAppSelector(selectWishlistCount);
  const dispatch = useAppDispatch();
  const { chrome, menuPersona } = useNavPersona();
  const { count: accountBadgeCount } = useNavbarBadges(
    menuPersona,
    isAuthenticated && isOpen,
  );

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (pathnameRef.current === location.pathname) return;
    pathnameRef.current = location.pathname;
    onClose();
  }, [location.pathname, onClose]);

  const isGhostClick = useCallback(() => {
    return Date.now() - openedAtRef.current < GHOST_CLICK_MS;
  }, [openedAtRef]);

  const handleGuardedClose = useCallback(() => {
    if (isGhostClick()) return;
    onClose();
  }, [isGhostClick, onClose]);

  const handleBackdropClose = useCallback(() => {
    if (isGhostClick()) return;
    onClose();
  }, [isGhostClick, onClose]);

  const openAccount = useCallback(() => {
    onClose();
    openAccountSheet();
  }, [onClose, openAccountSheet]);

  const handleCartOpen = useCallback(() => {
    requestCartHydration(dispatch);
    onClose();
  }, [dispatch, onClose]);

  const navLinks = chrome.centerLinks;
  const showQuickActions =
    chrome.showWishlistShortcut || chrome.showCartShortcut;

  // Prime compositor layers while closed so open/close is instant on first tap.
  useEffect(() => {
    const root = document.querySelector('.navbar-mobile');
    if (!(root instanceof HTMLElement)) return;

    const warm = () => {
      void root.offsetHeight;
      const panel = root.querySelector('.navbar-mobile__panel');
      const backdrop = root.querySelector('.navbar-mobile__backdrop');
      if (panel instanceof HTMLElement) void panel.offsetHeight;
      if (backdrop instanceof HTMLElement) void backdrop.offsetHeight;
    };

    warm();
    const id = requestAnimationFrame(warm);
    return () => cancelAnimationFrame(id);
  }, []);

  return createPortal(
    <div
      className="navbar-mobile"
      data-open={isOpen ? 'true' : 'false'}
      aria-hidden={!isOpen}
    >
      <button
        type="button"
        className="navbar-mobile__backdrop"
        aria-label="Close menu"
        tabIndex={isOpen ? 0 : -1}
        onClick={handleBackdropClose}
      />

      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
        className="navbar-mobile__panel"
      >
        <div className="navbar-mobile__orb navbar-mobile__orb--tr" aria-hidden />
        <div className="navbar-mobile__orb navbar-mobile__orb--bl" aria-hidden />

        <div className="navbar-mobile__header">
          <Link
            to="/"
            className="navbar-mobile__brand"
            onClick={onClose}
            {...getRoutePrefetchHandlers('/')}
          >
            <img
              src={qumashiLogo}
              alt="Qumashi"
              className="navbar-mobile__brand-img"
              width={32}
              height={32}
            />
            <div>
              <div className="navbar-mobile__brand-title">Qumashi</div>
              <div className="navbar-mobile__brand-sub">Menu</div>
            </div>
          </Link>

          <button
            type="button"
            className="navbar__menu-btn"
            data-open="true"
            aria-label="Close menu"
            onClick={handleGuardedClose}
          >
            <span className="navbar__menu-lines" aria-hidden>
              <span className="navbar__menu-line" />
              <span className="navbar__menu-line" />
              <span className="navbar__menu-line" />
            </span>
          </button>
        </div>

        <div className="navbar-mobile__body">
          {navLinks.length > 0 ? (
            <nav className="navbar-mobile__nav" aria-label="Primary">
              {navLinks.map((link: MainNavLink) => (
                <MobileNavRow
                  key={link.path}
                  link={link}
                  isActive={location.pathname.includes(link.path)}
                  onNavigate={onClose}
                />
              ))}
            </nav>
          ) : null}

          {showQuickActions ? (
            <div
              className={`navbar-mobile__quick navbar-mobile__item ${
                chrome.showWishlistShortcut ? 'navbar-mobile__quick--two' : ''
              }`}
            >
              {chrome.showWishlistShortcut ? (
                <QuickActionCard
                  to={ACCOUNT_WISHLIST_PATH}
                  onClick={onClose}
                  icon={HeartIcon}
                  label="Wishlist"
                  count={wishlistCount}
                  badgeClass={NAV_BADGE_WISHLIST_DRAWER}
                  iconClass="amber"
                />
              ) : null}
              {chrome.showCartShortcut ? (
                <QuickActionCard
                  to="/cart"
                  onClick={handleCartOpen}
                  icon={ShoppingBagIcon}
                  label="Cart"
                  count={cartItemCount}
                  badgeClass={NAV_BADGE_CART_DRAWER}
                  iconClass="indigo"
                />
              ) : null}
            </div>
          ) : null}

          <div className="navbar-mobile__footer navbar-mobile__item">
            {isAuthenticated && user ? (
              <MobileAccountIdentity
                user={user}
                variant="teaser"
                badgeCount={accountBadgeCount}
                onOpen={openAccount}
              />
            ) : (
              <GuestFooter onClose={onClose} />
            )}
          </div>
        </div>
      </aside>
    </div>,
    document.body,
  );
});

const MobileNavRow = memo(function MobileNavRow({
  link,
  isActive,
  onNavigate,
}: {
  link: MainNavLink;
  isActive: boolean;
  onNavigate: () => void;
}) {
  const meta = getLinkMeta(link.path);
  const Icon = meta.icon;
  const isFeatured = meta.featured;

  return (
    <div className="navbar-mobile__item">
      <Link
        to={link.path}
        onClick={onNavigate}
        {...getRoutePrefetchHandlers(link.path)}
        className={`navbar-mobile__row-link${
          isActive ? ' navbar-mobile__row-link--active' : ''
        }`}
      >
        <div
          className={`navbar-mobile__row-icon${
            isActive || isFeatured ? ' navbar-mobile__row-icon--featured' : ''
          }`}
        >
          <Icon className="navbar-mobile__row-icon-svg" />
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <p
            className={`navbar-mobile__row-title${
              isFeatured && !isActive
                ? ' navbar-mobile__row-title--featured'
                : ''
            }`}
          >
            {link.name}
          </p>
          <p className="navbar-mobile__row-sub">{meta.subtitle}</p>
        </div>
        <ChevronRightIcon className="navbar-mobile__row-chevron" />
      </Link>
    </div>
  );
});

function QuickActionCard({
  to,
  onClick,
  icon: Icon,
  label,
  count,
  badgeClass,
  iconClass,
}: {
  to: string;
  onClick: () => void;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  count: number;
  badgeClass: string;
  iconClass: 'amber' | 'indigo';
}) {
  return (
    <Link
      to={to}
      onClick={onClick}
      {...getRoutePrefetchHandlers(to)}
      className="navbar-mobile__quick-card"
    >
      <Icon
        className={`navbar-mobile__quick-icon ${
          iconClass === 'amber'
            ? 'navbar-mobile__quick-icon--amber'
            : 'navbar-mobile__quick-icon--indigo'
        }`}
      />
      <span className="navbar-mobile__quick-label">{label}</span>
      {count > 0 ? (
        <span className={`navbar-mobile__quick-badge ${badgeClass}`}>
          {count > 99 ? '99+' : count}
        </span>
      ) : null}
    </Link>
  );
}

function GuestFooter({ onClose }: { onClose: () => void }) {
  return (
    <div>
      <p className="navbar-mobile__guest-note">
        Join thousands of creators on Qumashi
      </p>
      <div className="navbar-mobile__guest-grid">
        <Link to="/login" onClick={onClose} className="navbar-mobile__guest-signin">
          Sign in
        </Link>
        <Link
          to="/register"
          onClick={onClose}
          className="navbar-mobile__guest-register"
        >
          <UserIcon className="h-4 w-4" />
          Get started
        </Link>
      </div>
    </div>
  );
}
